// @flow
import React, { Component } from 'react'
import { connect } from 'react-redux'
import * as operations from '../../redux/ducks/advertisementTable/reducers'
import { translateFunc, translations } from '../../shared/localize'
import _ from 'lodash'
import Datepicker from '../controls/Datepicker'
import styled, { type StyledComponent } from 'styled-components'
import getJson from '../../services/getJson'
import MultiSelect from '../controls/MultiSelect'
import type { OptionType } from '../types'
import Button from '../controls/PrimaryButton'
import Select from '../controls/Select'

const AdvertisementFilterForm: StyledComponent<Object, any, any> = styled.div`
  ${({ newAdvertisementPath }) => (newAdvertisementPath ? 'float: right' : 'float: left')};
  font-size: 14px;
  color: #000;
  display: flex;
  width: 840px;
  padding-bottom: 30px;
`
const AdvertisementFilterFormItem: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  line-height: 25px;
  height: 30px;
  padding: 0 5px;
`
const MultiSelectWrapper: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  padding: 0 5px;
  width: 30%;
`
const SelectWrapper: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  padding: 0 5px;
  width: 25%;
`
const MessageWrapper: StyledComponent<Object, any, any> = styled.div`
  float: right;
  padding-bottom: 10px;
`
const Message: StyledComponent<Object, any, any> = styled.span`
  color: #222;
  font-style: italic;
  font-size: 16px;
`
const AddButton: StyledComponent<Object, any, any> = styled(Button)`
  ${({ newAdvertisementPath }) => (newAdvertisementPath ? 'margin-left: 14px' : 'margin-left: 0')};
`

type Props = {|
  loadStatisticsPath: string,
  newAdvertisementPath: string,
  datepicker: string,
  dateSeparator: string,
  cookieStatisticsTypeKey: string,
  filterFormOrigin: string,
  providersList: Array<string>,
  statisticsType: string,
  advertisementTemplateOptions: Array<OptionType>,
  selectedAdvertisementTemplateIds: Array<number>,
  isImportedAudience: boolean,
  onChange: Function,
  onSelectedAdvertisementTemplateIdsChange: Function,
  dispatch: Function
|}

type State = {|
  statisticsType: string,
  statisticsTypeWas: string,
  datepicker: string,
  datepickerWas: string,
  wasDatesChanged: boolean,
  isLoading: boolean
|}

const t = translateFunc(translations.advertisement_table_filter_form)

class FilterForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    const statisticsType = props.statisticsType || ''

    this.state = {
      statisticsType,
      statisticsTypeWas: statisticsType,
      datepicker: props.datepicker,
      datepickerWas: props.datepicker,
      wasDatesChanged: false,
      isLoading: false
    }
  }

  isChanged() {
    return this.isDatepickerChanged() || this.isStatisticsTypeChanged()
  }

  change(state: Object) {
    const { onChange } = this.props

    this.setState(state, () => onChange(this.isChanged()))
  }

  isDatepickerChanged() {
    const { datepicker, datepickerWas } = this.state

    return datepicker !== datepickerWas
  }

  isStatisticsTypeChanged() {
    const { statisticsType, statisticsTypeWas } = this.state

    return statisticsType !== statisticsTypeWas
  }

  // changes store statisticsType when changed
  // changed store.statistics when changed
  onSubmit(event: any) {
    event.preventDefault()

    const { dispatch } = this.props
    const { isLoading, statisticsType, datepicker } = this.state

    if (isLoading) return

    this.setState({ isLoading: true }, () => {
      Promise.all([this.loadStatistics(), this.statisticsTypeObject()]).then(
        ([loadedStatisticsResponse, statisticsTypeResponse]) => {
          dispatch(
            operations.updateFromResponse(_.merge(loadedStatisticsResponse, statisticsTypeResponse))
          )
          this.change({
            datepickerWas: datepicker,
            statisticsTypeWas: statisticsType,
            isLoading: false
          })
        },
        (reason) => {
          console.error(reason)
          this.setState({ isLoading: false })
        }
      )
    })
  }

  loadStatistics() {
    const { loadStatisticsPath } = this.props
    const { datepicker } = this.state

    if (!this.isDatepickerChanged()) return Promise.resolve({})

    return new Promise<any>((resolve, reject) => {
      getJson(loadStatisticsPath, {
        datepicker
      })
        .done((response) => {
          resolve({
            statistics: response.data
          })
        })
        .fail((response) => {
          reject(response)
        })
    })
  }

  statisticsTypeObject() {
    const { statisticsType } = this.state

    if (this.isStatisticsTypeChanged()) {
      window.Cookies.set(this.props.cookieStatisticsTypeKey, statisticsType)
      return Promise.resolve({ statisticsType })
    } else {
      return Promise.resolve({})
    }
  }

  onDatesChange(value: any) {
    this.change({ datepicker: value })
  }

  renderMessage() {
    const { isLoading } = this.state
    const isChanged = this.isChanged()

    if (isLoading) {
      return <Message>{t('isLoading')}</Message>
    }

    if (isChanged) {
      return <Message>{t('isChanged')}</Message>
    }

    return ''
  }

  render() {
    const { isImportedAudience, newAdvertisementPath, dateSeparator, filterFormOrigin } = this.props
    const { statisticsType, datepicker, isLoading } = this.state

    const isChanged = this.isChanged()

    return (
      <div>
        {!isImportedAudience && newAdvertisementPath && (
          <AddButton beforeContent="+" small href={newAdvertisementPath}>
            {t('create_advertisement')}
          </AddButton>
        )}
        <AdvertisementFilterForm newAdvertisementPath={newAdvertisementPath}>
          <MultiSelectWrapper>
            <MultiSelect
              value={this.props.selectedAdvertisementTemplateIds}
              options={this.props.advertisementTemplateOptions}
              onChange={(ids) => this.props.onSelectedAdvertisementTemplateIdsChange(ids)}
              placeholder={t('template_options_placeholder')}
            />
          </MultiSelectWrapper>
          <SelectWrapper className="t-StatisticsFilterSelect">
            <Select
              disabled={isLoading}
              value={statisticsType}
              options={this.options()}
              onChange={(value) => this.change({ statisticsType: value })}
            />
          </SelectWrapper>
          <AdvertisementFilterFormItem>
            <span>{t('period')}</span>
          </AdvertisementFilterFormItem>
          <Datepicker
            disabled={isLoading}
            data={{
              align: 'right',
              initial: datepicker,
              separator: dateSeparator,
              origin: filterFormOrigin
            }}
            onChange={(value) => this.onDatesChange(value)}
            className="date-input_range JS-RangeDatepicker"
          />
          <AdvertisementFilterFormItem>
            <Button small disabled={!isChanged} onClick={(event) => this.onSubmit(event)}>
              {t('accept_btn')}
            </Button>
          </AdvertisementFilterFormItem>
        </AdvertisementFilterForm>
        <MessageWrapper>{this.renderMessage()}</MessageWrapper>
      </div>
    )
  }

  options(): Array<OptionType> {
    const defaultOptions = [
      { id: 'overall', name: t('overall') },
      { id: 'divided', name: t('divided') }
    ]
    const providerOptions = _.map(this.props.providersList, (providerName) => {
      return { id: providerName, name: t(providerName) }
    })
    return _.concat(defaultOptions, ...providerOptions)
  }
}

function mapStateToProps(state) {
  const { statisticsType, providersList } = state.advertisementTable

  return { statisticsType, providersList }
}

export default connect<Object, Object, _, _, _, _>(mapStateToProps)(FilterForm)
